import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, ArrowRightLeft, MessageCircle, Sparkles } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

export const ActivityPostCard = ({ post, onContact, isNew = false }) => {
  const {
    id,
    type,
    title,
    description,
    duration_hours,
    exchange_for,
    location,
    user,
    category,
    created_at,
  } = post;

  const isOffer = type === 'offer';
  const timeAgo = formatDistanceToNow(new Date(created_at), { addSuffix: true, locale: fr });
  const isRecent = (Date.now() - new Date(created_at).getTime()) < 3600000; // Less than 1 hour

  return (
    <Card 
      className={`group relative overflow-hidden rounded-2xl border bg-card transition-all duration-500 hover:shadow-xl hover:-translate-y-1 ${
        isNew ? 'activity-item-enter border-primary/30 bg-primary/5' : 'border-border hover:border-primary/20'
      }`}
      data-testid={`activity-post-${id}`}
    >
      {/* New indicator */}
      {isRecent && (
        <div className="absolute top-3 right-3 z-10">
          <Badge className="badge-new">
            <Sparkles className="w-3 h-3 mr-1" />
            Nouveau
          </Badge>
        </div>
      )}

      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <Link to={`/users/${user?.id}`} className="group/avatar">
            <Avatar className="h-12 w-12 ring-2 ring-background transition-all duration-300 group-hover/avatar:ring-primary/30 group-hover/avatar:scale-105">
              <AvatarImage src={user?.avatar_url} />
              <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                {user?.full_name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
          </Link>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Link 
                to={`/users/${user?.id}`}
                className="font-semibold hover:text-primary transition-colors"
              >
                {user?.full_name}
              </Link>
              <Badge 
                className={`transition-all duration-300 ${
                  isOffer 
                    ? 'bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground' 
                    : 'bg-secondary/10 text-secondary-foreground hover:bg-secondary'
                }`}
              >
                {isOffer ? '🎁 Propose' : '🔍 Recherche'}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">{timeAgo}</p>
          </div>
        </div>

        {/* Category */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xl transition-transform duration-300 group-hover:scale-125 group-hover:rotate-12">
            {category?.icon}
          </span>
          <span className="text-sm font-medium text-muted-foreground">{category?.name}</span>
        </div>

        {/* Title & Description */}
        <h3 className="font-semibold text-lg mb-2 group-hover:text-primary transition-colors duration-300">
          {title}
        </h3>
        {description && (
          <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
            {description}
          </p>
        )}

        {/* Exchange Info */}
        {exchange_for && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4 p-3 bg-muted/50 rounded-xl transition-colors duration-300 group-hover:bg-primary/5">
            <ArrowRightLeft className="w-4 h-4 text-primary flex-shrink-0" />
            <span>En échange de : <span className="font-medium text-foreground">{exchange_for}</span></span>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4" />
              {location}
            </span>
            <span className="flex items-center gap-1.5 credit-badge">
              <Clock className="w-4 h-4 text-secondary" />
              {duration_hours}h
            </span>
          </div>

          <Button
            onClick={(e) => {
              e.preventDefault();
              onContact?.(user?.id);
            }}
            className="btn-primary text-sm py-2 group/btn"
            data-testid={`contact-btn-${id}`}
          >
            <MessageCircle className="w-4 h-4 mr-1 transition-transform group-hover/btn:scale-110" />
            Contacter
          </Button>
        </div>
      </CardContent>

      {/* Decorative gradient on hover */}
      <div className="absolute inset-0 bg-gradient-to-t from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
    </Card>
  );
};

export default ActivityPostCard;
